import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { useHistory } from 'react-router-dom';
import { ErrorModal } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import { PendingModal } from '@/components';
import { SuccessModal } from '@/components/elements';
import useAddPaymentMethodQuery from '@/modules/transaction/components/ReimburseMe/queries/useAddPaymentMethod.query';
import { useGetPaymentMethods } from '@/modules/transaction/components/ReimburseMe/queries/useGetPaymentMethods.query';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import {
  PaymentMethod,
  PaymentMethodOwnerType,
  PaymentMethodVerificationStatus,
} from '@/modules/transaction/components/ReimburseMe/types/payment.types';
import { AllowedPaymentMethodConfig, PaymentMethodType } from '@/modules/user/user.types';
import { usePlaid } from '@/utils/plaid/hooks/usePlaid';
import PlaidModal from '@/utils/plaid/PlaidModal';
import { useDuplicatePlaidAccountStore } from '@/utils/plaid/store';

import ConfirmCancelModal from '../../ConfirmCancelModal';
import useAddPlaidPaymentMethod from '../../queries/useAddPlaidPaymentMethod.query';

import { PaymentMethodLayout } from './ChoosePaymentMethod/paymentMethodLayout.types';
import useBank from './DirectDeposit/BankForm/useBank.store';
import { BankInfo } from './DirectDeposit/BankForm/useBankFields';
import { useGetExternalBankAccountsQuery } from './DirectDeposit/BankForm/useGetBankAccountsList.query';
import { RemovePaymentMethodModal } from './RemovePaymentMethod/RemovePaymentMethodModal';
import ChoosePaymentMethod from './ChoosePaymentMethod';
import { DebitCardModal } from './DebitCart';
import DirectDeposit from './DirectDeposit';
import { EditPaymentMethodModal } from './EditPaymentMethod';
import ImmediatelyButton, { DefaultPaymentMethodBlockProps } from './ImmediatelyButton';
import { AddPayPalAccountModal } from './PayPal';
import useImmediatelyForm from './useImmediatelyForm';
import { AddVenmoAccountModal } from './Venmo';

export enum StepsStates {
  notInitiated = '',
  depositOrDebit = 'depositOrDebit',
  depositOptionsChoosing = 'depositOptionsChoosing',
  plaidOptionsChoosing = 'plaidOptionsChoosing',
  debitOptionsChoosing = 'debitOptionsChoosing',
  payPalOptionsChoosing = 'payPalOptionsChoosing',
  venmoOptionsChoosing = 'venmoOptionsChoosing',
  paymentMethodAdded = 'paymentMethodAdded',
}

enum EditStepsStates {
  notInitiated = '',
  removePaymentMethod = 'removePaymentMethod',
  editPaymentMethod = 'editPaymentMethod',
  paymentMethodRemoved = 'paymentMethodRemoved',
}

interface Props {
  isRefund?: boolean;
  isTitleHidden?: boolean;
  isLoading?: boolean;
  type: 'full' | 'small';
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  defaultStep?: StepsStates;
  onClose?: () => void;
  onSave?: (id?: string) => void;
  isUnwrapped?: boolean;
  isRejectionFlow?: boolean;
  isViewMode?: boolean;
  paymentMethodLayout?: PaymentMethodLayout;
  onGetPaymentMethods?: (paymentMethods: PaymentMethod[]) => void;
  renderWrapper?: (props: DefaultPaymentMethodBlockProps) => React.ReactNode;
  cardFooterRenderWrapper?: (paymentMethodType?: PaymentMethodType) => React.ReactNode;
}

const Immediately: React.FC<Props> = ({
  isRefund,
  isTitleHidden,
  isLoading,
  type,
  allowedPaymentMethodConfigs,
  defaultStep,
  onClose,
  onSave,
  isUnwrapped,
  isRejectionFlow,
  isViewMode,
  paymentMethodLayout,
  onGetPaymentMethods,
  renderWrapper,
  cardFooterRenderWrapper,
}) => {
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState('');
  const [errorModal, setErrorModal] = useState(false);
  const [isPlaidLoading, setIsPlaidLoading] = useState(false);
  const [isVerifyAccountFlow, setIsVerifyAccountFlow] = useState(false);
  const [step, setSteps] = useState(defaultStep || StepsStates.notInitiated);
  const [editStep, setEditSteps] = useState(EditStepsStates.notInitiated);
  const [message, setMessage] = useState<{ title?: string; desctiption?: string }>({});
  const [successVisible, setSuccessVisible] = useState(false);
  const [confirmCancelVisible, setConfirmCancelVisible] = useState(false);
  const { t } = useTranslation();
  const { save, isLoading: addCheckPaymentLoading } = useAddPaymentMethodQuery();
  const { isLoading: isPaymentMethodsLoading, refetch, formattedData } = useGetPaymentMethods(onGetPaymentMethods);

  const handleSetChecked = useStore((state) => state.handleSetChecked);
  const activeMethod = useStore((state) => state.activeMethod);
  const editableMethod = useStore((state) => state.editableMethod);
  const currentPaymentMethod = useStore((state) => state.activeMethod);
  const handleSetActivePaymentMethodById = useStore((state) => state.handleSetActivePaymentMethodById);

  const updateCurrentMethodAfterVerification = useCallback(async () => {
    const currentMethodId = currentPaymentMethod?.id;
    await refetch();
    handleSetActivePaymentMethodById(currentMethodId as string);
    setIsVerifyAccountFlow(false);
  }, [currentPaymentMethod?.id, handleSetActivePaymentMethodById, refetch]);

  const { setBankAccountData } = useBank();
  const { externalBankAccounts, isLoading: isBankAccountLoading } = useGetExternalBankAccountsQuery();
  const {
    fields,
    isLoading: isFormDataLoading,
    // isPaymentMethodExist,
  } = useImmediatelyForm(isViewMode ? undefined : () => setSteps(StepsStates.depositOrDebit), allowedPaymentMethodConfigs, isRefund);

  const isPendingAccountExists = useMemo(() => {
    const allDirectDepositMethods = formattedData?.filter(
      (method) => method.type === PaymentMethodType.DIRECT_DEPOSIT
    );
    return allDirectDepositMethods?.some(
      (method) =>
        method.verificationStatus === PaymentMethodVerificationStatus.PENDING ||
        method.verificationStatus === PaymentMethodVerificationStatus.PENDING_MANUAL
    );
  }, [formattedData]);

  const paymentEmployeeFee = useMemo(() => {
    const fee = allowedPaymentMethodConfigs?.find(
      (item) => item?.paymentMethodType === activeMethod?.type
    )?.paymentEmployeeFee;
    return fee !== undefined ? String(fee) : undefined;
  }, [allowedPaymentMethodConfigs, activeMethod]);

  useEffect(() => {
    setBankAccountData(externalBankAccounts[0] as BankInfo);
  }, [externalBankAccounts, setBankAccountData]);

  const handleRedirect = useCallback(() => {
    history.push(ROUTES.HOME);
  }, [history]);

  const chooseCheckHandler = useCallback(async () => {
    try {
      const data = await save({
        paymentType: PaymentMethodType.CHECK,
        paymentOwnerType: PaymentMethodOwnerType.EMPLOYEE,
      });
      handleSetChecked(`${data.id}`);
    } catch (error) {
      // @ts-ignore
      setErrorMessage(error?.response?.data?.elevate_error_message);
    }
  }, [save, handleSetChecked]);

  const handleRemoveSuccess = useCallback(() => {
    refetch();
    setEditSteps(EditStepsStates.paymentMethodRemoved);
  }, [refetch]);

  const handleEditSuccess = useCallback(() => {
    refetch();
    setEditSteps(EditStepsStates.notInitiated);
  }, [refetch]);

  const handleSaveSuccess = useCallback(() => {
    refetch();
    setSteps(StepsStates.paymentMethodAdded);
  }, [refetch]);

  const { save: plaidSave } = useAddPlaidPaymentMethod(handleSaveSuccess);

  const setDuplicateAccountModal = useDuplicatePlaidAccountStore((state) => state.setIsDuplicateModalVisible);

  const handlePlaidPaymentMethodSave = useCallback(
    async (plaidMetadata: PlaidLinkOnSuccessMetadata) => {
      try {
        setIsPlaidLoading(true);
        await plaidSave({
          plaidMetadata,
        });
        setIsPlaidLoading(false);
      } catch (e) {
        // @ts-ignore
        if (e.response.status === 409) {
          setIsPlaidLoading(false);
          setDuplicateAccountModal(true);
          return;
        }
        setErrorModal(true);
        setIsPlaidLoading(false);
      }
    },
    [plaidSave, setDuplicateAccountModal]
  );

  const { openPlaid, content } = usePlaid({
    onSuccess: handlePlaidPaymentMethodSave,
    onExit: () => setSteps(StepsStates.depositOrDebit),
    isPendingAccountExists,
  });

  const returnToDefaultStep = useCallback(() => {
    setSteps(StepsStates.notInitiated);
    if (onClose) onClose();
  }, [onClose, setSteps]);

  return (
    <>
      {step === StepsStates.depositOrDebit && (
        <ChoosePaymentMethod
          isLoading={isLoading}
          onClose={() => returnToDefaultStep()}
          chooseDeposit={() => {
            openPlaid();
            setSteps(StepsStates.plaidOptionsChoosing);
          }}
          chooseDebitCard={() => setSteps(StepsStates.debitOptionsChoosing)}
          choosePayPal={() => setSteps(StepsStates.payPalOptionsChoosing)}
          chooseVenmo={() => setSteps(StepsStates.venmoOptionsChoosing)}
          chooseCheck={chooseCheckHandler}
          removePaymentMethod={() => setEditSteps(EditStepsStates.removePaymentMethod)}
          editPaymentMethod={() => setEditSteps(EditStepsStates.editPaymentMethod)}
          onSave={onSave}
          allowedPaymentMethodConfigs={allowedPaymentMethodConfigs}
          isUnwrapped={isUnwrapped}
          isRejectionFlow={isRejectionFlow}
          isRefund={isRefund}
          paymentMethodLayout={paymentMethodLayout}
          cardFooterRenderWrapper={cardFooterRenderWrapper}
        />
      )}

      {step === StepsStates.plaidOptionsChoosing && content}

      {step === StepsStates.depositOptionsChoosing && (
        <DirectDeposit
          onClose={() => returnToDefaultStep()}
          onBack={() => setSteps(StepsStates.depositOrDebit)}
          onSuccess={() => setSteps(StepsStates.paymentMethodAdded)}
        />
      )}

      {step === StepsStates.payPalOptionsChoosing && (
        <AddPayPalAccountModal
          onClose={() => returnToDefaultStep()}
          onBack={() => setSteps(StepsStates.depositOrDebit)}
          onSuccess={() => setSteps(StepsStates.paymentMethodAdded)}
        />
      )}

      {step === StepsStates.venmoOptionsChoosing && (
        <AddVenmoAccountModal
          onClose={() => returnToDefaultStep()}
          onBack={() => setSteps(StepsStates.depositOrDebit)}
          onSuccess={() => setSteps(StepsStates.paymentMethodAdded)}
        />
      )}

      {step === StepsStates.debitOptionsChoosing && (
        <DebitCardModal
          onClose={() => returnToDefaultStep()}
          onBack={() => setSteps(StepsStates.depositOrDebit)}
          onSuccess={() => setSteps(StepsStates.paymentMethodAdded)}
        />
      )}

      {editStep === EditStepsStates.removePaymentMethod && (
        <RemovePaymentMethodModal
          onClose={() => setEditSteps(EditStepsStates.notInitiated)}
          onBack={() => setEditSteps(EditStepsStates.notInitiated)}
          onSuccess={handleRemoveSuccess}
          methodForRemove={editableMethod}
        />
      )}

      {editStep === EditStepsStates.editPaymentMethod && (
        <EditPaymentMethodModal
          onClose={() => setEditSteps(EditStepsStates.notInitiated)}
          onBack={() => setEditSteps(EditStepsStates.notInitiated)}
          onSuccess={handleEditSuccess}
          methodForEdit={editableMethod}
        />
      )}

      {isPlaidLoading && <PlaidModal visible isLoading />}

      <SuccessModal
        header={t('Payment method added successfully!')}
        buttonText={t('Got It!')}
        visible={step === StepsStates.paymentMethodAdded}
        onSetVisible={() => setSteps(StepsStates.depositOrDebit)}
        buttonStyle={{ width: '160px' }}
      />

      <SuccessModal
        header={t('Your payment method has been removed successfully!')}
        buttonText={t('Got It!')}
        visible={editStep === EditStepsStates.paymentMethodRemoved}
        onSetVisible={() => setEditSteps(EditStepsStates.notInitiated)}
        buttonStyle={{ width: '200px' }}
      />

      <ConfirmCancelModal
        visible={confirmCancelVisible}
        onSetVisible={setConfirmCancelVisible}
        onSubmit={handleRedirect}
      />
      <SuccessModal
        header={t('Your request is approved')}
        helptext={t(`Your payment will be issued as soon as possible based
        on your preferred payment method.`)}
        buttonText={t('Back to Account Details')}
        visible={successVisible}
        onSetVisible={setSuccessVisible}
        onSubmit={handleRedirect}
      />
      <PendingModal
        header={message?.title || ''}
        helptext={message?.desctiption}
        buttonText={t('Close')}
        visible={Boolean(Object.keys(message).length)}
        onSetVisible={() => setMessage({})}
      />

      <ErrorModal
        testId="payment-methods-error-modal"
        visible={errorModal}
        onSetVisible={() => {
          setErrorModal(false);
          setSteps(StepsStates.depositOrDebit);
        }}
        header={t('Something went wrong')}
        helpText={t('Try again later')}
        buttonText={t('Got It!')}
        onSubmit={() => {
          setErrorModal(false);
          setSteps(StepsStates.depositOrDebit);
        }}
      />

      {errorMessage ? (
        <ErrorModal
          testId="Immediately-error"
          visible
          header="Error"
          helpText={errorMessage}
          buttonText="Close"
          onSetVisible={() => {
            setErrorMessage('');
          }}
        />
      ) : null}

      <ImmediatelyButton
        isVerifyAccountFlow={isVerifyAccountFlow}
        isLoading={isLoading}
        isFormDataLoading={isFormDataLoading}
        isBankAccountLoading={isBankAccountLoading}
        addCheckPaymentLoading={addCheckPaymentLoading}
        isPaymentMethodsLoading={isPaymentMethodsLoading}
        defaultStep={defaultStep}
        type={type}
        isTitleHidden={isTitleHidden}
        fields={fields}
        currentPaymentMethod={currentPaymentMethod}
        paymentEmployeeFee={paymentEmployeeFee}
        setSteps={setSteps}
        updateCurrentMethodAfterVerification={updateCurrentMethodAfterVerification}
        setIsVerifyAccountFlow={setIsVerifyAccountFlow}
        renderWrapper={renderWrapper}
      />
    </>
  );
};
export default Immediately;
